import { SellarImportsPayoutGetResponseType } from "@sellar-io/api-v2/types";
import { useSellarCurrentBusiness } from "../../../../../hooks/useSellarCurrentBusiness.shared";
import { useState } from "react";
import dayjs from "dayjs";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Dropdown,
  DropdownButton,
  DropdownHeadingItem,
  DropdownItem,
  DropdownItems,
  Icon,
} from "@sellar-io/components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "../../../../../components/modal/Dialog";
import { AdminOnly } from "../../../../../components/authFilter/AdminOnly";
import { BasicDialog } from "../../../../../components/modal/BasicDialog";
import { useSellarImportRevenueInvoiceCreate } from "../hooks/useSellarImportRevenueInvoiceCreate";
import { downloadPDF } from "../../../../../utils/downloadHelpers";
import { SpinnerLegacy } from "../../../../../components";
import { Toast } from "../../../../../components/toast/Toast";
import { useToast } from "../../../../../components/toast/useToast.shared";
import { useSellarImportRevenueInvoiceDownload } from "../hooks/useSellarImportRevenueInvoiceDownload";
import { useSellarImportFeeInvoiceCreate } from "../hooks/useSellarImportFeeInvoiceCreate";
import { useSellarImportFeeInvoiceDownload } from "../hooks/useSellarImportFeeInvoiceDownload";
import { useSellarImportFeeInvoiceStatusUpdate } from "../hooks/useSellarImportFeeInvoiceStatusUpdate";
import { useSellarImportRevenueInvoiceStatusUpdate } from "../hooks/useSellarImportRevenueInvoiceStatusUpdate";

type RevenueInvoiceDocumentType = NonNullable<
  SellarImportsPayoutGetResponseType["RevenueInvoice"]
>;
type FeeInvoiceDocumentType = NonNullable<
  SellarImportsPayoutGetResponseType["FeeInvoice"]
>;

type DocumentType = RevenueInvoiceDocumentType | FeeInvoiceDocumentType;

export const SellarImportPayoutDocumentSummary = ({
  sellarImportPayoutId,
  revenueInvoice,
  feeInvoice,
}: {
  sellarImportPayoutId: number;
  revenueInvoice: RevenueInvoiceDocumentType | null;
  feeInvoice: FeeInvoiceDocumentType | null;
}) => {
  const { business } = useSellarCurrentBusiness();

  // dialog state
  // TODO: move all dialogs to the activeDialogData implementation

  const [isDownloadDialogActive, setIsDownloadDialogActive] = useState(false);
  const { toast } = useToast();

  const [activeDialogData, setActiveDialogData] = useState<{
    loading: boolean;
    onDismiss: () => void;
    onConfirm: () => void;
    confirmButtonText: string;
    title: string;
    description: string;
  }>();

  const [
    confirmRevenueInvoiceDialogActive,
    setConfirmRevenueInvoiceDialogActive,
  ] = useState(false);

  const [confirmFeeInvoiceDialogActive, setConfirmFeeInvoiceDialogActive] =
    useState(false);

  // update invoice status mutations

  const updateSellarImportFeeInvoiceStatusMutation =
    useSellarImportFeeInvoiceStatusUpdate({ sellarImportPayoutId });

  const updateSellarImportRevenueInvoiceStatusMutation =
    useSellarImportRevenueInvoiceStatusUpdate({ sellarImportPayoutId });

  // create invoice mutations

  const createSellarImportRevenueInvoiceMutation =
    useSellarImportRevenueInvoiceCreate({ id: sellarImportPayoutId });

  const createSellarImportFeeInvoiceMutation = useSellarImportFeeInvoiceCreate({
    id: sellarImportPayoutId,
  });

  // download invoice mutations

  const revenueInvoiceDownloadMutation =
    useSellarImportRevenueInvoiceDownload();

  const feeInvoiceDownloadMutation = useSellarImportFeeInvoiceDownload();

  const handleRevenueInvoiceDownload = ({
    document,
  }: {
    document: RevenueInvoiceDocumentType;
  }) => {
    setIsDownloadDialogActive(true);
    revenueInvoiceDownloadMutation.mutate(
      {
        sellarImportPayoutId,
      },
      {
        onSuccess: (data) => {
          const documentName = "Revenue invoice";
          const documentFileName = `${documentName} - ${document.customId} - ${dayjs(
            document.issuedAt ?? new Date(),
          ).format("DD-MM-YYYY")}.pdf`;

          downloadPDF(documentFileName, data.document);
          setIsDownloadDialogActive(false);
        },
        onError: () => {
          toast({
            placement: "bottom",
            render: () => (
              <Toast icon="ALERT_FILLED">
                Unable to download revenue invoice at this time
              </Toast>
            ),
          });
          setIsDownloadDialogActive(false);
        },
      },
    );
  };

  const handleFeeInvoiceDownload = ({
    document,
  }: {
    document: FeeInvoiceDocumentType;
  }) => {
    setIsDownloadDialogActive(true);
    feeInvoiceDownloadMutation.mutate(
      {
        sellarImportPayoutId,
      },
      {
        onSuccess: (data) => {
          const documentName = "Fee invoice";
          const documentFileName = `${documentName} - ${document.customId} - ${dayjs(
            document.issuedAt ?? new Date(),
          ).format("DD-MM-YYYY")}.pdf`;

          downloadPDF(documentFileName, data.document);
          setIsDownloadDialogActive(false);
        },
        onError: () => {
          toast({
            placement: "bottom",
            render: () => (
              <Toast icon="ALERT_FILLED">
                Unable to download revenue invoice at this time
              </Toast>
            ),
          });
          setIsDownloadDialogActive(false);
        },
      },
    );
  };

  if (!business) {
    return null;
  }

  return (
    <>
      <Card className="bg-white">
        <CardHeader className="pb flex flex-wrap items-center justify-between gap-2 bg-white p-4">
          <div className="flex flex-col gap-2">
            <h5 id="payoutDocuments" className="font-semibold text-zinc-800">
              Payout documents
            </h5>
            <p className="text-zinc-500">
              View and download your documents here
            </p>
          </div>

          <AdminOnly>
            <div className="flex flex-wrap gap-2">
              <Button
                onClick={() => setConfirmRevenueInvoiceDialogActive(true)}
                disabled={!!revenueInvoice}
              >
                <Icon icon="PLUS" /> Create total revenue invoice
              </Button>

              <Button
                onClick={() => setConfirmFeeInvoiceDialogActive(true)}
                disabled={!!feeInvoice}
              >
                <Icon icon="PLUS" /> Create fee invoice
              </Button>
            </div>
          </AdminOnly>
        </CardHeader>
        <CardContent className="p-4">
          <div className="flex flex-col gap-4">
            {revenueInvoice || feeInvoice ? (
              <>
                {revenueInvoice && (
                  <DocumentItem
                    document={revenueInvoice}
                    onDownload={handleRevenueInvoiceDownload}
                    onVoid={() => {
                      setActiveDialogData({
                        loading:
                          updateSellarImportRevenueInvoiceStatusMutation.isPending,
                        onDismiss: () => setActiveDialogData(undefined),
                        onConfirm: () => {
                          updateSellarImportRevenueInvoiceStatusMutation
                            .mutateAsync({
                              status: "void",
                            })
                            .then(() => {
                              setActiveDialogData(undefined);
                            });
                        },
                        confirmButtonText: "Proceed",
                        title: "Confirm you wish to void revenue invoice",
                        description: "Are you sure you wish to proceed?",
                      });
                    }}
                    onPaid={() => {
                      setActiveDialogData({
                        loading:
                          updateSellarImportRevenueInvoiceStatusMutation.isPending,
                        onDismiss: () => setActiveDialogData(undefined),
                        onConfirm: () => {
                          updateSellarImportRevenueInvoiceStatusMutation
                            .mutateAsync({
                              status: "paid",
                            })
                            .then(() => {
                              setActiveDialogData(undefined);
                            });
                        },
                        confirmButtonText: "Proceed",
                        title:
                          "Confirm you wish to mark revenue invoice as paid",
                        description: "Are you sure you wish to proceed?",
                      });
                    }}
                  />
                )}
                {feeInvoice && (
                  <DocumentItem
                    document={feeInvoice}
                    onDownload={handleFeeInvoiceDownload}
                    onVoid={() => {
                      setActiveDialogData({
                        loading:
                          updateSellarImportFeeInvoiceStatusMutation.isPending,
                        onDismiss: () => setActiveDialogData(undefined),
                        onConfirm: () => {
                          updateSellarImportFeeInvoiceStatusMutation
                            .mutateAsync({
                              status: "void",
                            })
                            .then(() => {
                              setActiveDialogData(undefined);
                            });
                        },
                        confirmButtonText: "Proceed",
                        title: "Confirm you wish to void fee invoice",
                        description: "Are you sure you wish to proceed?",
                      });
                    }}
                    onPaid={() => {
                      setActiveDialogData({
                        loading:
                          updateSellarImportFeeInvoiceStatusMutation.isPending,
                        onDismiss: () => setActiveDialogData(undefined),
                        onConfirm: () => {
                          updateSellarImportFeeInvoiceStatusMutation
                            .mutateAsync({
                              status: "paid",
                            })
                            .then(() => {
                              setActiveDialogData(undefined);
                            });
                        },
                        confirmButtonText: "Proceed",
                        title: "Confirm you wish to mark fee invoice as paid",
                        description: "Are you sure you wish to proceed?",
                      });
                    }}
                  />
                )}
              </>
            ) : (
              <div>No documents found</div>
            )}
          </div>
        </CardContent>
      </Card>
      <DownloadDialog
        active={isDownloadDialogActive}
        onDismiss={() => setIsDownloadDialogActive(false)}
      />

      <ConfirmCreateRevenueInvoiceDialog
        isSubmitting={createSellarImportRevenueInvoiceMutation.isPending}
        active={confirmRevenueInvoiceDialogActive}
        onDismiss={() => setConfirmRevenueInvoiceDialogActive(false)}
        onConfirm={() =>
          createSellarImportRevenueInvoiceMutation
            .mutateAsync()
            .then(() => setConfirmRevenueInvoiceDialogActive(false))
            .catch((error) => console.error(error))
        }
      />

      <ConfirmCreateFeeInvoiceDialog
        isSubmitting={createSellarImportFeeInvoiceMutation.isPending}
        active={confirmFeeInvoiceDialogActive}
        onDismiss={() => setConfirmFeeInvoiceDialogActive(false)}
        onConfirm={() =>
          createSellarImportFeeInvoiceMutation
            .mutateAsync()
            .then(() => setConfirmFeeInvoiceDialogActive(false))
            .catch((error) => console.error(error))
        }
      />

      <BasicDialog
        loading={activeDialogData?.loading}
        onDismiss={activeDialogData?.onDismiss}
        onConfirm={activeDialogData?.onConfirm}
        confirmButtonText={"Proceed"}
        dialogActive={Boolean(activeDialogData)}
        title={activeDialogData?.title}
        description={activeDialogData?.description}
      />
    </>
  );
};

type DocumentItemProps<T extends DocumentType> = {
  document: T;
  onDownload: (params: { document: T }) => void;
  onVoid: (params: { document: T }) => void;
  onPaid: (params: { document: T }) => void;
};

const DocumentItem = <T extends DocumentType>({
  document,
  onDownload,
  onVoid,
  onPaid,
}: DocumentItemProps<T>) => {
  const documentTitleText =
    document.type === "sellarImportFee" ? "Fee Invoice" : "Revenue Invoice";

  const documentMessages = [
    document.voidedAt &&
      `Voided on ${dayjs(document.voidedAt).format("DD/MM/YYYY")}`,
    document.paidAt && `Paid on ${dayjs(document.paidAt).format("DD/MM/YYYY")}`,
    document.issuedAt &&
      `Issued on ${dayjs(document.issuedAt).format("DD/MM/YYYY")}`,
    `Created on ${dayjs(document.createdAt).format("DD/MM/YYYY")}`,
  ].filter(Boolean);

  return (
    <div className="flex flex-1 items-center">
      <div className="flex flex-1 gap-4">
        <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-zinc-200">
          <Icon size={1.2} icon="FILE_DOCUMENT_CHECK_OUTLINE" />
        </div>
        <div className="flex flex-col">
          <span className="font-semibold">
            {documentTitleText} {document.customId}
          </span>
          <span>{documentMessages[0]}</span>
        </div>
      </div>
      <div className="flex items-center gap-2">
        <Icon
          onClick={() => onDownload({ document })}
          icon="TRAY_ARROW_DOWN"
          className="cursor-pointer"
        />
        {!["paid", "void"].includes(document.status) && (
          <Dropdown>
            <DropdownButton>
              <Button size={"small"} color="transparent">
                <Icon icon={"DOTS_VERTICAL"} />
              </Button>
            </DropdownButton>
            <DropdownItems>
              <DropdownHeadingItem className="bg-zinc-100 text-sm text-zinc-700">
                Edit
              </DropdownHeadingItem>

              <DropdownItem
                className="border-b border-zinc-200 text-sm text-zinc-600"
                onClick={() => {
                  onPaid({ document });
                }}
              >
                Mark as paid
              </DropdownItem>

              <DropdownItem
                className="border-b border-zinc-200 text-sm text-zinc-600"
                onClick={() => {
                  onVoid({ document });
                }}
              >
                Mark as void
              </DropdownItem>
            </DropdownItems>
          </Dropdown>
        )}
      </div>
    </div>
  );
};

type DownloadDialogProps = {
  active: boolean;
  onDismiss: () => void;
};
export const DownloadDialog = ({ onDismiss, active }: DownloadDialogProps) => {
  return (
    <Dialog active={active} onDismiss={onDismiss}>
      <DialogNavigation
        leftIcon="CHEVRON_DOWN"
        onClickLeftIcon={onDismiss}
        title="Downloading document"
      />
      <DialogContent>
        <SpinnerLegacy />
        <span>
          Download can take up to 10 seconds. Please do not navigate away until
          your download is complete.
        </span>
      </DialogContent>
    </Dialog>
  );
};

const ConfirmCreateRevenueInvoiceDialog = ({
  active,
  onDismiss,
  onConfirm,
  isSubmitting,
}: {
  active: boolean;
  onDismiss: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
}) => {
  return (
    <BasicDialog
      loading={isSubmitting}
      onDismiss={onDismiss}
      onConfirm={onConfirm}
      confirmButtonText={"Proceed"}
      dialogActive={active}
      title={"Create total revenue invoice"}
      description={"Are you sure you want to proceed?"}
    />
  );
};

const ConfirmCreateFeeInvoiceDialog = ({
  active,
  onDismiss,
  onConfirm,
  isSubmitting,
}: {
  active: boolean;
  onDismiss: () => void;
  onConfirm: () => void;
  isSubmitting: boolean;
}) => {
  return (
    <BasicDialog
      loading={isSubmitting}
      onDismiss={onDismiss}
      onConfirm={onConfirm}
      confirmButtonText={"Proceed"}
      dialogActive={active}
      title={"Create fee invoice"}
      description={"Are you sure you want to proceed?"}
    />
  );
};
